import { Grid, Typography } from "@material-ui/core";
import React from "react";
import {
    CartesianGrid,
    Line,
    LineChart,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { getRadonValue } from "../../../utils/getRadonValue";
import { getUncertainty } from "../../../utils/getUncertainty";
import ReportPageHeader from "./ReportPageHeader";
import { isCanadaReport } from "../../../utils/reportCountry";
import ReportPageFooter from "./ReportPageFooter";

const styles = {
    sectionTitle: {
        color: "black",
        fontSize: "11px",
        fontWeight: 700,
        fontStyle: "italic",
        textAlign: "left",
    },
    sectionDivider: {
        height: "4px",
        background: "#575195",
        marginBottom: 5,
        marginTop: 5,
    },
    labelCol: {
        color: "#333",
        fontSize: "10px",
        fontWeight: 400,
        textAlign: "left",
        lineHeight: "1.7",
    },
    valueCol: {
        color: "#111",
        fontSize: "10px",
        fontWeight: 500,
        textAlign: "left",
        lineHeight: "1.7",
    },
    tableHeader: {
        backgroundColor: "#E0E0E0",
        color: "black",
        fontSize: "10px",
        fontWeight: 600,
        textAlign: "center",
        padding: "6px 2px",
    },
    tableCell: {
        color: "#000",
        fontSize: "9px",
        fontWeight: 400,
        textAlign: "center",
        padding: "2px 2px",
        border: "1px solid #ddd",
        height: "25px",
    },
};

const SectionHeader = ({ title }) => (
    <div style={{ paddingLeft: 20, paddingRight: 20, marginTop: 10 }}>
        <div style={styles.sectionDivider} />
        <Typography style={styles.sectionTitle}>{title}</Typography>
    </div>
);

const InfoRow = ({ label, value }) => (
    <Grid container style={{ paddingLeft: 35, paddingRight: 20 }}>
        <Grid item xs={6}>
            <Typography style={styles.labelCol}>{label}</Typography>
        </Grid>
        <Grid item xs={6}>
            <Typography style={styles.valueCol}>{value ?? "N/A"}</Typography>
        </Grid>
    </Grid>
);



const PageChart = ({ theme, reportDetails, reportChartRadonValue, chartHeight = 180 }) => {
    if (!reportDetails) return null;
    const isCanada = isCanadaReport(reportDetails);

    const measurementUnitVal =
        reportDetails.measurementUnit ?? reportDetails.measurement_unit ?? 0;
    const radonAvg = reportDetails.radonAvg ?? reportDetails.avg_radon;
    const highest = reportDetails.highest ?? reportDetails.highest_radon;
    const lowest = reportDetails.lowest ?? reportDetails.lowest_radon;

    const reformatRadon = (val) => getRadonValue(measurementUnitVal, val);
    const renderRadonValue = (val) => {
        const formatted = reformatRadon(val);
        if (formatted === null || formatted === undefined || formatted === "") return "N/A";

        if (!isCanada) return formatted;
        if (measurementUnitVal === 0) return `${formatted} pCi/L`;
        return (
            <>
                {formatted} Bq/m<sup>3</sup>
            </>
        );
    };

    const uncertainty = getUncertainty(radonAvg);

    const is48h = reportDetails?.req_type === "RPT_48H" || reportDetails?.measurement_duration === "48 Hours";
    const sourceData = is48h ? (reportChartRadonValue || []).slice(0, 48) : (reportChartRadonValue || []);

    const chartData = sourceData.map((item, idx) => ({
        ...item,
        data_no: idx + 1,
    }));

    return (
        <div style={{ height: "750px", overflow: "hidden", position: "relative" }}>
            {/* ===== Shared Header ===== */}
            <ReportPageHeader reportDetails={reportDetails} />

            {/* ===== HISTORICAL RADON TREND ===== */}
            <div style={{ paddingLeft: 20, paddingRight: 20, marginTop: 5 }}>
                <Typography style={styles.sectionTitle}>
                    {isCanada
                        ? "Historical Radon Trend/Evolution historique du taux de radon"
                        : "Historical Radon Trend"}
                </Typography>
            </div>
            <div style={{ marginTop: 6, paddingLeft: 10 }}>
                <LineChart
                    style={{ backgroundColor: "white" }}
                    width={560}
                    height={200}
                    data={chartData}
                    margin={{ top: 5, right: 20, left: -20, bottom: 0 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="data_no"
                        tick={{ fontSize: 9 }}
                        interval={Math.max(0, Math.floor((chartData?.length || 0) / 24) - 1)}
                    />
                    <YAxis tick={{ fontSize: 9 }} />
                    <Tooltip
                        wrapperStyle={{
                            backgroundColor: "white",
                            outline: "none",
                            boxShadow: "2px 3px 10px rgba(53,64,82,0.2)",
                            fontSize: 10,
                        }}
                    />
                    <Line
                        type="monotone"
                        dataKey="radon_level"
                        stroke="#3DB5E6"
                        dot={{ r: 2 }}
                        strokeWidth={1.5}
                    />
                </LineChart>
            </div>

            {/* ===== RADON CONCENTRATION ===== */}
            <SectionHeader title={isCanada ? "Radon Concentration/Concentration en radon" : "Radon Concentration"} />
            <InfoRow
                label={
                    isCanada ? (
                        <>
                            Measurement Radon Concentration Max/
                            <br />
                            Concentration maximale de radon mesuree
                        </>
                    ) : (
                        "Measurement Radon Concentration Max"
                    )
                }
                value={renderRadonValue(highest)}
            />
            <InfoRow
                label={
                    isCanada ? (
                        <>
                            Measurement Radon Concentration Average/
                            <br />
                            Concentration moyenne de radon mesuree
                        </>
                    ) : (
                        "Measurement Radon Concentration Average"
                    )
                }
                value={renderRadonValue(radonAvg)}
            />
            <InfoRow
                label={
                    isCanada ? (
                        <>
                            Measurement Radon Concentration Min/
                            <br />
                            Concentration minimale de radon mesuree
                        </>
                    ) : (
                        "Measurement Radon Concentration Min"
                    )
                }
                value={renderRadonValue(lowest)}
            />
            <InfoRow label={isCanada ? "% Uncertainty/% d'incertitude" : "% Uncertainty"} value={uncertainty} />


            <ReportPageFooter reportDetails={reportDetails} />
        </div>
    );
};

export default PageChart;
